import React, { useState } from 'react';
import {
  SectionHeading,
  StyledAsterisksText,
  SubSection,
  StyledInput,
  inputBorderStyle,
  FormSection,
  DescriptionText,
  InputColumnSection,
  InputRow,
} from '@/shared/styles/styledComponents/editProfileForm';
import { green, placeholder } from '@/shared/styles/colors';
import { ProfileFormDataWithoutCheckBox } from '@/shared/types/stylist';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import ErrorFrame from '@/shared/components/ErrorFrame';
import { PHONE_NUMBER_MASK } from '@/shared/constants/utility';
import { InputLabel } from '@/shared/components/StaticInput';
import MaskInput from 'react-native-mask-input';
import { isValidPhoneNumber } from 'libphonenumber-js';

interface NotificationSectionProps {
  profileFormData: ProfileFormDataWithoutCheckBox;
  setProfileFormData: React.Dispatch<
    React.SetStateAction<ProfileFormDataWithoutCheckBox>
  >;
}

const NotificationSection: React.FC<NotificationSectionProps> = ({
  profileFormData,
  setProfileFormData,
}) => {
  const { isDesktop } = useBreakpoint();
  const [phoneNumberError, setPhoneNumberError] = useState(false);
  const [isPhoneInputFocused, setIsPhoneInputFocused] = useState(false);

  const handlePhoneFocus = () => setIsPhoneInputFocused(true);

  const handlePhoneBlur = () => {
    setIsPhoneInputFocused(false);
    const phoneNumber = profileFormData.phoneNumber?.trim() || '';

    setPhoneNumberError(
      phoneNumber.length > 0 && !isValidPhoneNumber(`+1${phoneNumber}`, 'US'),
    );
  };

  const handlePhoneChange = (masked: string, unmasked: string) => {
    const isValid =
      isValidPhoneNumber(`+1${unmasked}`, 'US') || !unmasked.length;

    if (phoneNumberError && isValid) {
      setPhoneNumberError(false);
    }

    setProfileFormData((prev) => ({
      ...prev,
      maskedPhoneNumber: masked,
      phoneNumber: unmasked,
    }));
  };

  return (
    <FormSection isDesktop={isDesktop}>
      <SectionHeading isDesktop={isDesktop}>Notification</SectionHeading>
      <DescriptionText marginBottom={18}>
        We&apos;ll only contact to send important notifications and updates
        about your sessions. Your number won&apos;t be shared with clients or
        third parties.
      </DescriptionText>
      <SubSection marginBottom={isDesktop ? 0 : 4}>
        {/* Half width, lined up with the left column of the rows above */}
        <InputRow isDesktop={isDesktop}>
          <InputColumnSection isDesktop={isDesktop}>
            <StyledAsterisksText>Phone Number (Optional)</StyledAsterisksText>
            <ErrorFrame
              errorText="Invalid phone number"
              errorVisible={phoneNumberError}
            >
              <InputLabel
                isVisible={
                  Boolean(profileFormData.phoneNumber) || isPhoneInputFocused
                }
              >
                Number
              </InputLabel>
              <StyledInput
                onFocus={handlePhoneFocus}
                onBlur={handlePhoneBlur}
                outlineStyle={inputBorderStyle}
                mode="outlined"
                value={profileFormData.phoneNumber}
                textColor={green}
                placeholder="Enter phone number here"
                placeholderTextColor={placeholder}
                inputMode="numeric"
                render={(inputProps) => (
                  <MaskInput
                    {...inputProps}
                    style={inputProps.style}
                    value={profileFormData.phoneNumber}
                    onChangeText={handlePhoneChange}
                    mask={PHONE_NUMBER_MASK}
                  />
                )}
              />
            </ErrorFrame>
          </InputColumnSection>
          {isDesktop ? <InputColumnSection isDesktop /> : null}
        </InputRow>
      </SubSection>
    </FormSection>
  );
};

export default NotificationSection;
